import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { TrendData } from '@/hooks/useDashboardMetrics';

interface TrendChartProps {
  data: TrendData[] | undefined;
  isLoading: boolean;
}

const TrendChart = ({ data, isLoading }: TrendChartProps) => {
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Tendências dos Últimos 7 Dias
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) return null;

  // Calcular estatísticas
  const totalOpened = data.reduce((sum, item) => sum + item.opened, 0);
  const totalResolved = data.reduce((sum, item) => sum + item.resolved, 0);
  const avgOpened = Math.round(totalOpened / data.length);
  const avgResolved = Math.round(totalResolved / data.length);
  const trend = totalResolved >= totalOpened ? 'positive' : 'negative';

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{label}</p>
          <div className="space-y-1">
            {payload.map((entry: any, index: number) => (
              <p key={entry.name || `entry-${index}`} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {entry.value} tickets
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const getTrendIcon = () => {
    if (trend === 'positive') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'negative') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-gray-600" />;
  };

  const getTrendColor = () => {
    if (trend === 'positive') return 'text-green-600';
    if (trend === 'negative') return 'text-red-600';
    return 'text-gray-600';
  };

  const getTrendText = () => {
    if (trend === 'positive') return 'Tendência positiva';
    if (trend === 'negative') return 'Requer atenção';
    return 'Estável';
  };

  return (
    <Card className="h-full hover:shadow-lg transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-600">
            <TrendingUp className="h-5 w-5" />
            Tendências dos Últimos 7 Dias
          </div>
          <div className={`flex items-center gap-1 text-sm ${getTrendColor()}`}>
            {getTrendIcon()}
            {getTrendText()}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Gráfico */}
        <div className="h-64 mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                stroke="#666"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#666"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="opened" 
                stroke="#ef4444" 
                strokeWidth={2}
                dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2 }}
                name="Abertos"
              />
              <Line 
                type="monotone" 
                dataKey="resolved" 
                stroke="#22c55e" 
                strokeWidth={2}
                dot={{ fill: '#22c55e', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#22c55e', strokeWidth: 2 }}
                name="Resolvidos"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 font-medium">Tickets Abertos</p>
                <p className="text-xs text-red-600">Média diária</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-red-600">{avgOpened}</div>
                <div className="text-xs text-red-500">Total: {totalOpened}</div>
              </div>
            </div>
          </div>

          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-medium">Tickets Resolvidos</p>
                <p className="text-xs text-green-600">Média diária</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-green-600">{avgResolved}</div>
                <div className="text-xs text-green-500">Total: {totalResolved}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Indicador de Performance */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">Taxa de Resolução</p>
              <p className="text-xs text-gray-600">Resolvidos vs Abertos</p>
            </div>
            <div className="text-right">
              <div className={`text-lg font-bold ${getTrendColor()}`}>
                {totalOpened > 0 ? Math.round((totalResolved / totalOpened) * 100) : 0}%
              </div>
              <p className="text-xs text-gray-500">
                {totalResolved >= totalOpened ? 'Acima da meta' : 'Abaixo da meta'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrendChart;
